import React from 'react';
import { Button, Group, Loader, Text } from '@mantine/core';
import { IconRefresh } from '@tabler/icons-react';
import { useDelayedFlag } from '@/hooks/use.delayed.flag';
import { useEndReached } from '@/hooks/use.end.reached';
import type { TypeListError, TypeListMore } from '@/types/list/types.list';

type Props = {
    summary?: React.ReactNode;
    more?: TypeListMore;
    refreshing: boolean;
    error?: TypeListError;
};

const ListStatus: React.FC<Props> = ({ summary, more, refreshing, error }) => {
    const hasMore = Boolean(more?.hasMore);
    const loadingMore = Boolean(more?.loading);
    const failed = Boolean(more?.failed);
    const auto = more?.auto ?? true;
    const showRefresh = useDelayedFlag(refreshing && !loadingMore);

    const sentinel = useEndReached(() => {
        if (more && !more.loading && !more.failed) more.onMore();
    }, auto && hasMore && !failed);

    const stale = Boolean(error?.onRetry);
    if (!summary && !hasMore && !loadingMore && !showRefresh && !stale) return null;

    return (
        <>
            {hasMore ? <div ref={sentinel} className="bd-list-sentinel" aria-hidden /> : null}
            <div className="bd-list-status">
                <Text size="xs" c="dimmed" aria-live="polite" style={{ minWidth: 0 }}>
                    {showRefresh ? (
                        <Group component="span" gap={6} wrap="nowrap"><Loader size={12} /> Aggiorno…</Group>
                    ) : summary}
                </Text>
                <Group gap="xs" wrap="nowrap">
                    {stale && error?.onRetry ? (
                        <Button variant="subtle" size="xs" color="danger" leftSection={<IconRefresh size={14} />} loading={error.retrying} onClick={error.onRetry}>
                            Non aggiornato: riprova
                        </Button>
                    ) : null}
                    {loadingMore ? (
                        <Group gap={8} wrap="nowrap"><Loader size="xs" /><Text size="xs" c="dimmed">Carico…</Text></Group>
                    ) : hasMore && more ? (
                        <Button variant="default" size="xs" color={failed ? 'danger' : undefined} onClick={more.onMore}>
                            {failed ? 'Non sono arrivati: riprova' : more.label ?? 'Carica altri'}
                        </Button>
                    ) : null}
                </Group>
            </div>
        </>
    );
};

export default ListStatus;
